import { membershipDay, membershipUseLine, type ManualPaymentMethod, type MembershipSummary, type SessionsLeft } from "@teamna/shared";
import { useEffect, useState } from "react";
import { Badge, Button, Field, InlineError, Notice, Panel, RadioGroup, SessionsLeftBar, showToast } from "../../components/index.ts";
import { newIdempotencyKey } from "@teamna/web-client/api/client.ts";
import { useOwnerCancelMembership, useRecordMembershipRenewal } from "@teamna/web-client/hooks/use-owner.ts";
import { formatMoney } from "@teamna/web-client/lib/format.ts";
import { ProofField, useProofUpload } from "./proof-field.tsx";

/**
 * The membership on the client page (M2): the month, what it used of the cap, the renewal request
 * and its state, and the owner's actions. Plain props, so Storybook draws it.
 */
export function MembershipBlock({
  membership,
  onCopyLink,
  onCancel,
}: {
  membership: MembershipSummary;
  onCopyLink?: (url: string) => void;
  onCancel?: () => void;
}) {
  const renewal = membership.renewal;
  const next = membershipDay(membership.nextStartsOn);
  const line =
    membership.state === "cancelling"
      ? `Cancelled by ${membership.cancelledBy === "member" ? "the client" : "the business"}. It ends on ${next} and does not renew.`
      : membership.state === "unpaid"
        ? "This month is not paid. The client cannot book sessions in it until it is paid. Sessions booked before stay."
        : renewal
          ? `Next month from ${next}: ${formatMoney(renewal.amountFils)}, ${renewal.status === "overdue" ? "late" : "not paid yet"}.`
          : `Renews on ${next}. Teamna asks for the payment before that day.`;

  return (
    <div className="flex flex-col gap-3 rounded-card border border-line px-4 py-3" aria-label="Membership">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-ink" dir="auto">
              {membership.name}
            </span>
            <Badge tone="info">Membership</Badge>
            {membership.state === "cancelling" ? <Badge tone="quiet">Cancelled</Badge> : null}
            {membership.state === "unpaid" ? <Badge tone="late">Not paid</Badge> : null}
          </p>
          <p className="text-[13px] text-ink-2">
            {membershipUseLine({ used: membership.used, cap: membership.cap })}. {formatMoney(membership.priceFils)} a month.
          </p>
        </div>
      </div>
      {/* The bar of the attendance register (#ai-feedback 2026-10-30), for the month in force. */}
      {membership.periodEndsAt ? <SessionsLeftBar facts={monthFacts(membership)} /> : null}
      <p className="text-[13px] text-ink-2">{line}</p>
      {/* The month is paid with Record a payment on the client card: one button for every payment (card #5). */}
      {onCancel || (onCopyLink && renewal?.payLink) ? (
        <div className="flex flex-wrap gap-2">
          {renewal?.payLink && onCopyLink ? (
            <Button variant="secondary" onClick={() => onCopyLink(renewal.payLink!)}>
              Copy the payment link
            </Button>
          ) : null}
          {onCancel ? (
            <Button variant="secondary" onClick={onCancel}>
              {membership.state === "cancelling" ? "End it now" : "Cancel the membership"}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** The register's facts for the month in force: its limit and what is left, nothing taken today. */
function monthFacts(membership: MembershipSummary): SessionsLeft {
  return {
    kind: "month",
    size: membership.cap ?? 0,
    left: membership.cap === null ? 0 : Math.max(0, membership.cap - membership.used),
    today: "none",
    paused: false,
    // The block says "Not paid" itself.
    unpaid: false,
    unlimited: membership.cap === null,
  };
}

/**
 * The membership on an owner page, with its actions: the client page and each child card of the
 * family page (#ai-feedback 2026-10-30: a swimmer's membership was not shown on the family page).
 */
export function OwnerMembership({
  businessId,
  participantId,
  name,
  membership,
  canCancel,
}: {
  businessId: string;
  participantId: string;
  name: string;
  membership: MembershipSummary;
  canCancel: boolean;
}) {
  const action = useMembershipAction(membership);
  return (
    <>
      <MembershipBlock
        membership={membership}
        {...(canCancel ? { onCancel: () => action.open("cancel") } : {})}
        onCopyLink={(url) => {
          void navigator.clipboard
            ?.writeText(url)
            .then(() => showToast("Payment link copied"))
            .catch(() => showToast("Copy did not work. Open the Payments tab to send the link."));
        }}
      />
      {action.action === "cancel" ? (
        <CancelMembershipPanel businessId={businessId} participantId={participantId} name={name} membership={membership} onClose={action.close} />
      ) : null}
    </>
  );
}

/**
 * Record the next month of a membership, paid in cash or by bank transfer, with an optional proof
 * of payment. The "membership month" step of Record a payment (`record-payment.tsx`).
 */
export function RecordRenewalPanel({
  businessId,
  participantId,
  name,
  membership,
  onBack,
  animate = true,
  onClose,
}: {
  businessId: string;
  participantId: string;
  name: string;
  membership: MembershipSummary;
  /** Back to "What is the payment for?". */
  onBack?: (() => void) | undefined;
  animate?: boolean;
  onClose: () => void;
}) {
  const record = useRecordMembershipRenewal(businessId);
  const proof = useProofUpload(businessId);
  const [idempotencyKey] = useState(() => newIdempotencyKey());
  const [method, setMethod] = useState<ManualPaymentMethod>("cash");
  const [note, setNote] = useState("");
  const renewal = membership.renewal!;

  return (
    <Panel
      open
      onClose={onClose}
      title={`Record a payment for ${name}`}
      subtitle="The next month of the membership"
      animate={animate}
      actions={
        <>
          <Button
            loading={record.isPending}
            disabled={proof.uploading}
            onClick={() =>
              record.mutate(
                {
                  participantId,
                  method,
                  requestId: renewal.requestId,
                  ...(note.trim() ? { note: note.trim() } : {}),
                  ...(proof.proofId ? { proofId: proof.proofId } : {}),
                  idempotencyKey,
                },
                {
                  onSuccess: () => {
                    showToast(`${formatMoney(renewal.amountFils)} recorded. The month from ${membershipDay(renewal.dueOn)} is paid.`);
                    onClose();
                  },
                },
              )
            }
          >
            Record {formatMoney(renewal.amountFils)}
          </Button>
          <Button variant="secondary" onClick={onBack ?? onClose}>
            {onBack ? "Back" : "Cancel"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <div>
          <p className="text-[15px] font-semibold text-ink">
            {membership.name}, {formatMoney(renewal.amountFils)}
          </p>
          <p className="text-[14px] text-ink-2">The month from {membershipDay(renewal.dueOn)}.</p>
        </div>
        <RadioGroup<ManualPaymentMethod>
          legend="How did they pay?"
          options={[
            { value: "cash", label: "Cash" },
            { value: "transfer", label: "Bank transfer" },
          ]}
          value={method}
          onChange={setMethod}
        />
        <Field
          label="Note, optional"
          hint="The client does not see this. It shows in Payments."
          dir="auto"
          value={note}
          maxLength={200}
          placeholder={method === "transfer" ? "Transfer reference" : "Receipt number"}
          onChange={(event) => setNote(event.target.value)}
        />
        <ProofField upload={proof} memberName="The client" />
        <p className="rounded-card bg-accent-surface px-4 py-3 text-[15px] text-ink" aria-live="polite">
          The month is paid and the client can book its sessions. The reminders stop. The client gets a message.
        </p>
        {record.error ? <InlineError error={record.error} /> : null}
      </div>
    </Panel>
  );
}

/**
 * The membership panel the owner opened, tied to what it opened for: Cancel to the membership,
 * Record to the open renewal request of the month. It shows only while the profile still has that
 * same membership or request: a read that shows none, and then a newer one, never opens the old
 * confirmation for the new target (review F6.1, F7.1).
 */
export function useMembershipAction(membership: MembershipSummary | null): {
  action: "record" | "cancel" | null;
  open: (kind: "record" | "cancel") => void;
  close: () => void;
} {
  const [opened, setOpened] = useState<{ kind: "record" | "cancel"; target: string } | null>(null);
  // Read at each render: once the profile shows another target (or none), the action is gone for
  // good, because a new membership or request never has the old id.
  const current = opened !== null && membershipActionTarget(membership, opened.kind) === opened.target;
  return {
    action: current ? opened!.kind : null,
    open: (kind) => {
      const target = membershipActionTarget(membership, kind);
      if (target) setOpened({ kind, target });
    },
    close: () => setOpened(null),
  };
}

function membershipActionTarget(membership: MembershipSummary | null, kind: "record" | "cancel"): string | null {
  if (!membership) return null;
  return kind === "cancel" ? membership.id : (membership.renewal?.requestId ?? null);
}

/** Cancel a membership: at the end of the paid month, or at once. */
export function CancelMembershipPanel({
  businessId,
  participantId,
  name,
  membership,
  onClose,
}: {
  businessId: string;
  participantId: string;
  name: string;
  membership: MembershipSummary;
  onClose: () => void;
}) {
  const cancel = useOwnerCancelMembership(businessId);
  const cancelling = membership.state === "cancelling";
  const [at, setAt] = useState<"period_end" | "now">(cancelling ? "now" : "period_end");
  // The panel cancels the membership it opened for. A read of the profile can show a newer
  // membership while it is open: the panel then closes, and the owner opens it again for the new
  // one (review F5.1).
  const [targetId] = useState(membership.id);
  const replaced = membership.id !== targetId;
  useEffect(() => {
    if (replaced) onClose();
  }, [replaced, onClose]);
  const next = membershipDay(membership.nextStartsOn);

  return (
    <Panel
      open
      onClose={onClose}
      title={`Cancel the membership of ${name}`}
      actions={
        <>
          <Button
            variant={at === "now" ? "danger" : "primary"}
            loading={cancel.isPending}
            disabled={replaced}
            onClick={() =>
              cancel.mutate(
                { participantId, at, membershipId: targetId },
                {
                  onSuccess: () => {
                    showToast(at === "now" ? "The membership ended." : `The membership ends on ${next}.`);
                    onClose();
                  },
                },
              )
            }
          >
            {at === "now" ? "End it now" : `Cancel from ${next}`}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Keep it
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <RadioGroup<"period_end" | "now">
          legend="When does it end?"
          options={[
            ...(cancelling
              ? []
              : [
                  {
                    value: "period_end" as const,
                    label: `At the end of the month, on ${next}`,
                    hint: "No new payment request. The client books the sessions of the paid month as usual.",
                  },
                ]),
            {
              value: "now" as const,
              label: "Now",
              hint: "No new session can be booked from it. Sessions already booked stay. Teamna gives no money back: refund in Payments if you agree to.",
            },
          ]}
          value={at}
          onChange={setAt}
        />
        {membership.renewal ? (
          <Notice tone="info" title="The open payment request is cancelled">
            {formatMoney(membership.renewal.amountFils)} for the month from {membershipDay(membership.renewal.dueOn)}. The reminders stop.
          </Notice>
        ) : null}
        {cancel.error ? <InlineError error={cancel.error} /> : null}
      </div>
    </Panel>
  );
}
